import { useState } from 'react';
import PageHeader from '../components/PageHeader';
import { Card, NumberField } from '../components/Form';
import { useDialog } from '../components/Dialog';
import { adminManualRefund } from '../lib/api';

export default function RefundsPage() {
  const [orderId, setOrderId] = useState('');
  const [amountDollars, setAmountDollars] = useState<number | undefined>(
    undefined
  );
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const { confirm, dialog } = useDialog();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await confirm({
      title: 'Issue refund',
      message: `Refund ${
        amountDollars ? `$${amountDollars.toFixed(2)}` : 'the full amount'
      } for order ${orderId.trim()}? This calls Stripe immediately and cannot be undone.`,
      destructive: true,
      confirmLabel: 'Issue refund',
    });
    if (!ok) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await adminManualRefund({
        orderId: orderId.trim(),
        amountCents: amountDollars
          ? Math.round(amountDollars * 100)
          : undefined,
        reason: reason.trim() || undefined,
      });
      setResult({
        kind: 'success',
        message: `Refund ${res.data.refundId} for $${(res.data.amountCents / 100).toFixed(
          2
        )} succeeded.`,
      });
      setOrderId('');
      setAmountDollars(undefined);
      setReason('');
    } catch (e) {
      setResult({
        kind: 'error',
        message: `Refund failed: ${e instanceof Error ? e.message : String(e)}`,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Manual Refunds"
        subtitle="Issue full or partial refunds via Stripe. All actions are audit-logged."
      />
      {dialog}
      <Card title="Issue refund">
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block text-sm sm:col-span-2">
            <div className="mb-1 font-medium text-slate-700">Order ID</div>
            <input
              type="text"
              required
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="orderId from Orders page"
              className="w-full rounded border border-slate-300 px-3 py-1.5 font-mono text-sm"
            />
          </label>
          <NumberField
            label="Amount (leave blank for full refund)"
            value={amountDollars}
            onChange={setAmountDollars}
            step={0.01}
            min={0}
            suffix="$"
          />
          <label className="block text-sm">
            <div className="mb-1 font-medium text-slate-700">Reason</div>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="customer_request | fraud | duplicate | …"
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm"
            />
          </label>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={busy || !orderId.trim()}
              className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {busy ? 'Processing…' : 'Issue refund'}
            </button>
          </div>
          {result && (
            <p
              className={`sm:col-span-2 rounded-md border px-3 py-2 text-sm ${
                result.kind === 'success'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-rose-200 bg-rose-50 text-rose-800'
              }`}
              role={result.kind === 'error' ? 'alert' : 'status'}
            >
              {result.message}
            </p>
          )}
        </form>
      </Card>
    </>
  );
}
